import { cn } from "@/lib/utils";
import { useCallback, useEffect, useRef } from "react";

interface VerificationCodeInputProps {
  value: string[];
  onChange: (value: string[]) => void;
  onComplete: (code: string) => void;
  error?: boolean;
  disabled?: boolean;
}

const DIGIT_COUNT = 6;

export const VerificationCodeInput = ({
  value,
  onChange,
  onComplete,
  error = false,
  disabled = false,
}: VerificationCodeInputProps) => {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Focus the first empty input on mount
  useEffect(() => {
    const firstEmpty = value.findIndex((v) => !v);
    const targetIndex = firstEmpty === -1 ? 0 : firstEmpty;
    inputRefs.current[targetIndex]?.focus();
  }, []);

  const triggerComplete = useCallback(
    (digits: string[]) => {
      const code = digits.join("");
      if (code.length === DIGIT_COUNT && digits.every((d) => d !== "")) {
        onComplete(code);
      }
    },
    [onComplete],
  );

  const handleChange = (index: number, inputValue: string) => {
    if (disabled) return;

    // Only allow numeric input
    const digit = inputValue.replace(/\D/g, "").slice(-1);
    if (!digit) return;

    const newValue = [...value];
    newValue[index] = digit;
    onChange(newValue);

    // Auto-advance to next input
    if (index < DIGIT_COUNT - 1) {
      inputRefs.current[index + 1]?.focus();
    }

    triggerComplete(newValue);
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (disabled) return;

    if (e.key === "Backspace") {
      e.preventDefault();
      const newValue = [...value];

      if (value[index]) {
        // Clear current input
        newValue[index] = "";
        onChange(newValue);
      } else if (index > 0) {
        // Move to previous input and clear it
        newValue[index - 1] = "";
        onChange(newValue);
        inputRefs.current[index - 1]?.focus();
      }
    } else if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < DIGIT_COUNT - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    if (disabled) return;

    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").replace(/\D/g, "");
    if (!pastedData) return;

    const digits = pastedData.slice(0, DIGIT_COUNT).split("");
    const newValue = [...value];

    digits.forEach((digit, i) => {
      if (i < DIGIT_COUNT) {
        newValue[i] = digit;
      }
    });

    onChange(newValue);

    // Focus the input after the last pasted digit, or the last one
    const nextIndex = Math.min(digits.length, DIGIT_COUNT - 1);
    inputRefs.current[nextIndex]?.focus();

    triggerComplete(newValue);
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    e.target.select();
  };

  const renderInput = (index: number) => (
    <input
      key={index}
      ref={(el) => {
        inputRefs.current[index] = el;
      }}
      type="text"
      inputMode="numeric"
      autoComplete="one-time-code"
      maxLength={1}
      value={value[index] || ""}
      onChange={(e) => handleChange(index, e.target.value)}
      onKeyDown={(e) => handleKeyDown(index, e)}
      onPaste={handlePaste}
      onFocus={handleFocus}
      disabled={disabled}
      className={cn(
        "w-10 h-12 text-center text-lg font-semibold rounded-md border-2 bg-inner-box outline-none transition-colors",
        error
          ? "border-error focus:border-error"
          : "border-dark-tertiary/30 focus:border-primary-action",
        disabled && "opacity-50 cursor-not-allowed",
      )}
      aria-label={`Digit ${index + 1}`}
    />
  );

  return (
    <div className="flex items-center justify-center gap-x-1.5">
      {renderInput(0)}
      {renderInput(1)}
      {renderInput(2)}
      <span className="text-dark-tertiary text-lg font-semibold mx-0.5">-</span>
      {renderInput(3)}
      {renderInput(4)}
      {renderInput(5)}
    </div>
  );
};
